/**
 * Consensus Analysis Trigger Component
 * Phase 4 of Consensus Analysis Implementation
 *
 * Displays button to trigger GPT-4o semantic analysis
 * Shows status indicators for processing/completed/failed states
 */

import React, { useEffect, useState } from 'react';
import { ScaleIcon } from '@heroicons/react/24/outline';
import { TokenQuotaExhaustedModal } from '../../../components/shared/TokenQuotaExhaustedModal';
import { useConsensusAnalysis } from '../hooks/useConsensusAnalysis';
import { useConsensusAnalysisContext } from '../context/ConsensusAnalysisContext';

interface ConsensusAnalysisTriggerProps {
  comparisonId: string;
  onAnalysisComplete?: () => void;
}

export function ConsensusAnalysisTrigger({
  comparisonId,
  onAnalysisComplete
}: ConsensusAnalysisTriggerProps) {
  const sharedContext = useConsensusAnalysisContext();
  const {
    status,
    isLoading,
    error,
    checkStatus,
    triggerAnalysis,
    tokenQuotaExceeded,
    tokenQuotaInfo
  } = sharedContext || useConsensusAnalysis(comparisonId);
  const [showTokenQuotaModal, setShowTokenQuotaModal] = useState(false);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    if (status?.status === 'completed' && onAnalysisComplete) {
      onAnalysisComplete();
    }
  }, [status?.status, onAnalysisComplete]);

  // Show modal when token quota is exceeded
  useEffect(() => {
    if (tokenQuotaExceeded) {
      setShowTokenQuotaModal(true);
    }
  }, [tokenQuotaExceeded]);

  if (status?.status === 'completed') {
    return null;
  }

  if (status?.status === 'failed') {
    return (
      <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg border border-red-200">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>Analysis failed</span>
        <button
          onClick={triggerAnalysis}
          className="underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  // Show helpful message for POC limitation
  if (error && error.includes('POC Limitation')) {
    return (
      <div className="flex flex-col gap-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-2">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <h3 className="font-medium text-blue-900 text-sm mb-1">
              Detailed Consensus Analysis - POC Limitation
            </h3>
            <p className="text-sm text-blue-700 mb-2">
              This feature currently supports 2-model comparisons only. Processing multiple models
              requires more time than our serverless functions allow.
            </p>
            <p className="text-xs text-blue-600">
              Tip: Create a new comparison with just 2 models to try this feature.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-2">
        <button
          onClick={triggerAnalysis}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-lg transition-colors font-medium text-sm w-fit disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Analyzing sentences...</span>
            </>
          ) : (
            <>
              <ScaleIcon className="w-4 h-4" />
              <span>Run Consensus Analysis</span>
            </>
          )}
        </button>

        {error && (
          <p className="text-xs text-red-500">{error}</p>
        )}
      </div>

      {/* Token Quota Exhausted Modal - Blocking for consensus analysis */}
      {showTokenQuotaModal && tokenQuotaInfo && (
        <TokenQuotaExhaustedModal
          isOpen={showTokenQuotaModal}
          onClose={() => setShowTokenQuotaModal(false)}
          usedTokens={tokenQuotaInfo.used}
          totalTokens={tokenQuotaInfo.limit}
          planName={tokenQuotaInfo.planName}
          blocking={false}
        />
      )}
    </>
  );
}
